// File: src/utils/colorUtils.ts
// ===================================================
// FINAL, UPDATED FILE

/**
 * A simple hashing function to convert a string to a number.
 */
const stringToHash = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash);
};

/**
 * Generates a visually distinct color for a string using the golden angle.
 * This ensures that successively generated colors are far apart on the color wheel.
 * 
 * @param str The string to generate a color for.
 * @returns An HSL color code string.
 */
export const getColorForString = (str: string): string => {
    if (!str) {
        return 'hsl(0, 0%, 80%)'; // A neutral grey for fallback
    }
    const hash = stringToHash(str);
    
    // Using the golden angle (approx 137.5 degrees) provides a good distribution of hues.
    const hue = (hash * 137.508) % 360; 
    
    // Keep saturation and lightness in a pleasant, non-garish range.
    const saturation = 75; 
    const lightness = 60;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Creates a map of group names to colors.
 * 
 * @param groups An array of unique group names.
 * @returns An object mapping each group name to a color string.
 */
export const createColorMap = (groups: string[]): { [key: string]: string } => {
    const colorMap: { [key: string]: string } = {};
    groups.forEach(group => {
        colorMap[group] = getColorForString(group);
    });
    return colorMap;
};